/**
 * Map technical validator messages to business-friendly copy.
 */

const CYCLE_RE = /Circular dependency:\s*(.+)/i;
const DUP_ROLE_RE = /Semantic role\s+(\S+)\s+mapped to multiple fields:\s*(\S+)\s+and\s*(\S+)/i;
const MISSING_ROLE_RE = /requires semantic role\s+(\S+)/i;
const MISSING_REF_RE = /Field\s+(\S+)\s+references missing field\s+(.+)$/i;
const DUP_FIELD_RE = /Duplicate field code:\s*(\S+)/i;
const DUP_SECTION_RE = /Duplicate section code:\s*(\S+)/i;
const DIV_ZERO_RE = /Field\s+(\S+):\s*DIVIDE by constant zero/i;
const NO_FORMULA_RE = /Formula field\s+(\S+)\s+has no formulaJson/i;

const ROLE_LABELS = {
  GROSS_UPTO_DATE: 'Gross Upto Date',
  CURRENT_RA_VALUE: 'Current RA Value',
  TOTAL_RECOVERIES: 'Total Recoveries',
  TOTAL_WITHHELD: 'Total Withheld',
  NET_PAYABLE: 'Net Payable',
  TOTAL_DEDUCTIONS: 'Total Deductions',
};

const roleLabel = (r) => ROLE_LABELS[r] || r;

/**
 * @returns {{ severity: 'error'|'warning'|'ok', message: string, fieldCode?: string, relatedFieldCode?: string, raw: string }}
 */
export const translateValidationMessage = (raw, fields = []) => {
  const text = String(raw || '');
  const byCode = Object.fromEntries((fields || []).map((f) => [f.fieldCode, f]));
  const labelOf = (code) => byCode[code]?.fieldLabel || code;

  let m = text.match(CYCLE_RE);
  if (m) {
    const chain = m[1].split(/\s*→\s*|\s*->\s*/).map((s) => s.trim()).filter(Boolean);
    const pretty = chain.map(labelOf).join(' → ');
    return {
      severity: 'error',
      message: `This calculation depends on itself through other fields (${pretty}). Remove the loop before saving.`,
      fieldCode: chain[0],
      relatedFieldCode: chain[1],
      raw: text,
      kind: 'CYCLE',
    };
  }

  m = text.match(DUP_ROLE_RE);
  if (m) {
    return {
      severity: 'error',
      message: `${roleLabel(m[1])} is already assigned to "${labelOf(m[2])}" and "${labelOf(m[3])}". Only one field can have this system meaning.`,
      fieldCode: m[3],
      relatedFieldCode: m[2],
      raw: text,
      kind: 'DUP_ROLE',
    };
  }

  m = text.match(MISSING_ROLE_RE);
  if (m) {
    return {
      severity: 'error',
      message: `System meaning "${roleLabel(m[1])}" is required before publishing. Assign it on one field.`,
      raw: text,
      kind: 'MISSING_ROLE',
      role: m[1],
    };
  }

  m = text.match(MISSING_REF_RE);
  if (m) {
    const missing = String(m[2] || '').trim().replace(/\.$/, '');
    return {
      severity: 'error',
      message: `"${labelOf(m[1])}" uses "${missing}", but that field code was not found. Use the field's internal code (e.g. TOTAL_AMOUNT), not its display name.`,
      fieldCode: m[1],
      raw: text,
      kind: 'MISSING_REF',
    };
  }

  m = text.match(DUP_FIELD_RE);
  if (m) {
    return {
      severity: 'error',
      message: `Two fields share the internal code ${m[1]}. Each field needs a unique code.`,
      fieldCode: m[1],
      raw: text,
      kind: 'DUP_FIELD',
    };
  }

  m = text.match(DUP_SECTION_RE);
  if (m) {
    return {
      severity: 'error',
      message: `Two sections share the code ${m[1]}. Rename one section.`,
      raw: text,
      kind: 'DUP_SECTION',
    };
  }

  m = text.match(DIV_ZERO_RE);
  if (m) {
    return {
      severity: 'error',
      message: `"${labelOf(m[1])}" divides by zero. Change the rate or divisor.`,
      fieldCode: m[1],
      raw: text,
      kind: 'DIV_ZERO',
    };
  }

  m = text.match(NO_FORMULA_RE);
  if (m) {
    return {
      severity: 'error',
      message: `"${labelOf(m[1])}" is marked as calculated but has no calculation rule yet.`,
      fieldCode: m[1],
      raw: text,
      kind: 'NO_FORMULA',
    };
  }

  return {
    severity: 'error',
    message: text,
    raw: text,
    kind: 'UNKNOWN',
  };
};

export const groupValidationResults = (validation, fields = []) => {
  const errors = (validation?.errors || []).map((e) => translateValidationMessage(e, fields));
  const warnings = (validation?.warnings || []).map((w) => ({
    ...translateValidationMessage(w, fields),
    severity: 'warning',
  }));

  const softWarnings = [];
  (fields || []).forEach((f) => {
    const cfg = f.metadata?.businessConfig;
    if (!cfg) return;
    if (cfg.behaviourType === 'PERCENTAGE' || cfg.behaviourType === 'CAPPED_PERCENTAGE') {
      if (cfg.rateValue == null || cfg.rateValue === '') {
        softWarnings.push({
          severity: 'warning',
          message: `"${f.fieldLabel || f.fieldCode}" has no rate.`,
          fieldCode: f.fieldCode,
          kind: 'NO_RATE',
        });
      }
      if (!cfg.baseFieldCode) {
        softWarnings.push({
          severity: 'warning',
          message: `"${f.fieldLabel || f.fieldCode}" has no calculation base.`,
          fieldCode: f.fieldCode,
          kind: 'NO_BASE',
        });
      }
    }
  });

  return {
    ok: Boolean(validation?.ok) && errors.length === 0,
    errors,
    warnings: [...warnings, ...softWarnings],
    readyChecks: [
      { ok: !errors.some((e) => e.kind === 'DUP_FIELD'), label: 'No duplicate field codes' },
      { ok: !errors.some((e) => e.kind === 'CYCLE'), label: 'No circular calculations' },
      { ok: !errors.some((e) => e.kind === 'MISSING_ROLE' || e.kind === 'DUP_ROLE'), label: 'Required system meanings assigned' },
      { ok: !errors.some((e) => e.kind === 'NO_FORMULA' || e.kind === 'MISSING_REF'), label: 'All formulas valid' },
    ],
  };
};
